import { useCallback, useEffect, useMemo, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { WALL_COLOR_PRESETS } from '../lib/floorPlanDesignerState.js';
import { FLOORPLAN_TEXTURE_CATALOG, getTexturesForCategory } from '../lib/floorPlanTextureCatalog.js';
import { normalizeHexColor, normalizeMaterialConfig } from '../lib/floorPlanMaterialConfig.js';
import FloorPlanMaterialNoneThumbnail from './FloorPlanMaterialNoneThumbnail.jsx';
import FloorPlanEditModal from './FloorPlanEditModal.jsx';
import FloorPlanSegmentedToggle from './FloorPlanSegmentedToggle.jsx';

/**
 * @param {{
 *   targetLabel: string;
 *   category: 'wall' | 'floor';
 *   initialMaterial: import('../lib/floorPlanMaterialConfig.js').FloorPlanMaterialConfig;
 *   initialInsideMaterial?: import('../lib/floorPlanMaterialConfig.js').FloorPlanMaterialConfig;
 *   initialOutsideMaterial?: import('../lib/floorPlanMaterialConfig.js').FloorPlanMaterialConfig;
 *   onApply: (
 *     material: import('../lib/floorPlanMaterialConfig.js').FloorPlanMaterialConfig,
 *     wallSide?: 'inside' | 'outside',
 *   ) => void;
 *   onClose: () => void;
 * }} props
 */
function FloorPlanMaterialPickerPopup({
  targetLabel,
  category,
  initialMaterial,
  initialInsideMaterial,
  initialOutsideMaterial,
  onApply,
  onClose,
}) {
  const normalizedInitial = useMemo(
    () => normalizeMaterialConfig(initialMaterial),
    [initialMaterial],
  );
  const normalizedInsideInitial = useMemo(
    () => normalizeMaterialConfig(initialInsideMaterial ?? initialMaterial),
    [initialInsideMaterial, initialMaterial],
  );
  const normalizedOutsideInitial = useMemo(
    () => normalizeMaterialConfig(initialOutsideMaterial ?? initialMaterial),
    [initialOutsideMaterial, initialMaterial],
  );

  const [wallSide, setWallSide] = useState('inside');
  const [mode, setMode] = useState(normalizedInitial.materialMode);
  const [draftHex, setDraftHex] = useState(normalizedInitial.color);
  const [textureId, setTextureId] = useState(normalizedInitial.textureId);

  const activeSideInitial =
    category === 'wall'
      ? wallSide === 'inside'
        ? normalizedInsideInitial
        : normalizedOutsideInitial
      : normalizedInitial;

  useEffect(() => {
    setMode(activeSideInitial.materialMode);
    setDraftHex(activeSideInitial.color);
    setTextureId(activeSideInitial.textureId);
  }, [activeSideInitial]);

  const textures = useMemo(
    () => (category === 'floor' ? getTexturesForCategory('floor') : FLOORPLAN_TEXTURE_CATALOG),
    [category],
  );

  const previewColor = normalizeHexColor(draftHex) || activeSideInitial.color;
  const isNoneSelected = mode === 'color' || textureId === null;

  useEffect(() => {
    if (mode !== 'texture' || !textures.length) return;
    if (textureId === null) return;
    if (!textures.some((entry) => entry.id === textureId)) {
      setTextureId(textures[0]?.id ?? null);
    }
  }, [mode, textures, textureId]);

  const handleApply = useCallback(() => {
    const resolvedTextureId = mode === 'texture' && textureId ? textureId : null;
    const material = normalizeMaterialConfig(
      {
        materialMode: resolvedTextureId ? 'texture' : 'color',
        color: previewColor,
        textureId: resolvedTextureId,
      },
      previewColor,
    );
    onApply(material, category === 'wall' ? wallSide : undefined);
    onClose();
  }, [mode, previewColor, textureId, category, wallSide, onApply, onClose]);

  const selectNone = useCallback(() => {
    setTextureId(null);
    setMode('color');
  }, []);

  const selectTexture = useCallback((id) => {
    setTextureId(id);
    setMode('texture');
  }, []);

  return (
    <FloorPlanEditModal
      title={`${targetLabel} material`}
      ariaLabel={`${targetLabel} material`}
      onClose={onClose}
      onApply={handleApply}
      className={mode === 'texture' ? 'floor-plan-material-picker-popup--texture' : ''}
    >
      <div className="floor-plan-opening-edit-fields">
        {category === 'wall' ? (
          <FloorPlanSegmentedToggle
            value={wallSide}
            options={[
              { value: 'inside', label: 'Inside Wall' },
              { value: 'outside', label: 'Outside Wall' },
            ]}
            onChange={(next) => setWallSide(next === 'outside' ? 'outside' : 'inside')}
            ariaLabel="Wall side"
            className="floor-plan-material-picker-segment"
            role="tablist"
            optionRole="tab"
          />
        ) : null}

        <FloorPlanSegmentedToggle
          value={mode}
          options={[
            { value: 'color', label: 'Pure Color' },
            { value: 'texture', label: 'Image Texture' },
          ]}
          onChange={(next) => {
            const nextMode = next === 'texture' ? 'texture' : 'color';
            setMode(nextMode);
            if (nextMode === 'texture' && textureId === null && textures.length > 0) {
              setTextureId(textures[0].id);
            }
          }}
          ariaLabel="Material type"
          className="floor-plan-material-picker-segment"
          role="tablist"
          optionRole="tab"
        />

        {mode === 'color' ? (
          <div className="new-design-hex-picker-panel floor-plan-wall-color-picker">
            <HexColorPicker
              color={previewColor}
              onChange={(hex) => setDraftHex(normalizeHexColor(hex) || draftHex)}
            />
            <input
              type="text"
              className="new-2d-project-input"
              value={previewColor}
              onChange={(e) => setDraftHex(e.target.value)}
              aria-label="Hex color"
            />
            <div className="floor-plan-wall-color-swatches" role="list" aria-label="Preset colors">
              {WALL_COLOR_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  className="floor-plan-wall-color-swatch"
                  style={{ backgroundColor: preset }}
                  aria-label={`Color ${preset}`}
                  onClick={() => setDraftHex(preset)}
                />
              ))}
            </div>
            <div
              className="floor-plan-material-preview-swatch"
              style={{ backgroundColor: previewColor }}
              aria-hidden
            />
          </div>
        ) : (
          <div className="floor-plan-material-texture-grid">
            <button
              type="button"
              className={`floor-plan-material-texture-card floor-plan-material-texture-card--none${
                isNoneSelected ? ' floor-plan-material-texture-card--selected' : ''
              }`}
              onClick={selectNone}
            >
              <FloorPlanMaterialNoneThumbnail />
              <span className="floor-plan-material-texture-card-label">None</span>
            </button>
            {textures.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={`floor-plan-material-texture-card${
                  !isNoneSelected && textureId === entry.id
                    ? ' floor-plan-material-texture-card--selected'
                    : ''
                }`}
                onClick={() => selectTexture(entry.id)}
              >
                <img src={entry.source} alt={entry.label} />
              </button>
            ))}
          </div>
        )}
      </div>
    </FloorPlanEditModal>
  );
}

export default FloorPlanMaterialPickerPopup;
